/** @module csi/widget/SvgCanvasEditor */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/Deferred",
	"dojo/promise/all",
	"dojo/sniff",
	"dojo/topic",
	"dojo/when",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/_Widget",
	"dijit/ToolbarSeparator",
	"canvas/Constants",
	"canvas/widgets/interfaces/model/enumerations/PlaceholderTextFormat",
	"csi",
	"csi/_base/underscore",
	"csi/_base/storage",
	"csi/action",
	"csi/canvas/storeFactory",
	"csi/card/_base",
	"csi/def/Action",
	"csi/heading/_base",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/core/Key",
	"csi/reflection",
	"csi/state/rootStore",
	"csi/sessionConstants",
	"csi/types",
	"csi/widget/_DndHTML5Mixin",
	"csi/xml",
	"csi/datasheet/Note",
	"./SvgImageDropController",
	"./_svg/DropTarget",
	"./_svg/Events",
	"./_svg/PluginFactory",
	"./_svg/util",
	"dojo/text!./SvgCanvas.html",
	"csi/widget/Container",
	"csi/widget/SplitPane"
], function(declare, lang, Deferred, all, sniff, topic, when, _TemplatedMixin, _WidgetsInTemplateMixin, _Widget, ToolbarSeparator, {PLACEHOLDER_RICH_TEXT_DIV},
	{PlaceholderTextFormat}, csi, _, storage, csiAction, storeFactory, csiCard, Action, csiHeading, html, csiImage, locale, Key, reflection, {sessionStore}, sessionConstants,
	types, _DndHTML5Mixin, xml, Note, SvgImageDropController, DropTarget, Events, PluginFactory, svgUtil, template) {

	var DEFAULT_SPLIT_PCT = 0.62;

	/**
	 * @typedef {Object} SelectionTarget
	 * This object contains a DOMNode to select and the target
	 * offset to select within it.
	 * @property {DOMNode} element
	 * @property {number} offset
	 */

	/**
	 * Send data to canvas app to display placeholders.
	 * @typedef {object} PlaceholderData
	 * Represents data used to create image and attribute place holders.
	 * The format of the object is following:
	 * @property {string} type - placeholder type (Colorway, Style, etc.)
	 * @property {string[]} attributeKeys - array of strings for attribute keys
	 * @property {string[]} labeledImageKeys - array of strings for labeled image keys
	 * @property {string} defaultImageKey - string for default image key
	 */

	/**
	 * Canvas data used on the c8 side.
	 * @typedef {object} CanvasData
	 * Represents data on objects on the c8 canvas.
	 * The format of the object is following:
	 * @property {boolean} isPlaceholder - indicates if canvas object is a placeholder
	 * @property {boolean} isTextShape - indicates if canvas object is a text shape
	 * @property {string} shape - shape of the current object being manipulated
	 */

	/**
	 * Given an element, return all its base-level text nodes in order
	 * @param {JQuery} element
	 * @returns {JQuery[]}
	 */
	function getConsecutiveTextNodes(element) {
		var result = [];

		// Check if nodes are text nodes (nodeType == 3) and that they have >0 length
		if (element[0] && element[0].nodeType == 3 && element[0].length > 0) {
			return [element];
		}
		else {
			element.contents().each(function(e, val) {
				result = result.concat(getConsecutiveTextNodes($(val)));
			});
		}

		return result;
	};

	return declare([_Widget, _TemplatedMixin, _WidgetsInTemplateMixin, _DndHTML5Mixin], {
		templateString: template,

		// CanvasEditor.CanvasApp: Instance to store editor.
		canvasApp: null,

		/** @property {CanvasData} Data on selected shape tool, whether or not shape is placeholder, and whether or not shape is text shape. */
		canvasData: {},

		// Boolean: shows if canvas is in editable mode or not
		editMode: false,

		// String: name of previous version storage key use to save defaults
		oldStorageKey: "svgCanvas",

		// String: name of localStorage key to use to save defaults
		storageKey: "svgCanvasStorageKey",

		canvasNode: null,
		insertNode: null,
		notesNode: null,
		splitNode: null,

		// Object: map of toolbar widgets by plugin name
		_plugins: null,

		// Boolean: used to indicate whether canvas is dirty or not, used while user in text edit mode and save canvas
		isTextDirty: false,

		// Boolean: used to indicate whether canvas placeholders have been modified, used when user has accepted changes to placeholders or has chosen to keep existing
		placeholdersModified: false,

		// String: previous placeholder text format
		previousPlaceholderTextFormat: "",

		// Shape[]: previously selected shapes
		previousSelections: null,

		// Boolean: indicates if orientation of canvas and note pane is vertical (true); default is horizontal (false)
		vertical: false,

		// Number: current zoom level
		zoom: 1,

		// Object: page content or shapes saved to clipboard for cut, copy, paste
		clipboard: null,

		// Object: map of shape attributes to special functions for getting/setting the attribute
		_attrGetters: null,
		_attrSetters: null,

		/** @property {DndSource} _dndTarget - target object to handle drag and drop of images */
		_dndTarget: null,

		// Shape[]: selected shapes
		shapeSelections: null,

		// Object: undo/redo stacks
		opsStack: null,

		// String[]: ordered list of page URLs as they were when editing started, or preceding last change that can be undone
		originalSortList: null,

		// Object: map of original canvas content when first start editing
		originalContent: null,

		// Array: array of pages to be rendered. This is used while rendering existing pages.
		pages: [],

		// String: store HTML for the currently rendered page. This is used while pushing contents to Undo stack.
		_pageHtml: null,

		/** @property {IPlaceholderDifferenceData[]} placeholderDifferenceData - Attribute placeholder data values,
		 * differences, and flag indicating invalid placeholders for deletion
		 * Actual structure of this object is outer and inner arrays:
		 * - outer array objects are one per canvas page
		 * - inner arrays objects are one per page placeholder group
		 * - deepest array objects are one per placeholder in group */
		placeholderDifferenceData: null,

		// Placeholders that should be deleted.
		placeholderDeletions: null,

		/** @property {Boolean} placeholderDataChanged set to true if at least one attribute placeholder
		 * on the canvas (all pages) has a data value that has changed (user reviews and chooses to apply or not) */
		placeholderDataChanged: false,

		// extension points
		onBlur: function() { },
		onChange: function() { },

		/**
		 * Adds the objects from the given node to the canvas
		 * @param {Object} node
		 */
		addObjectsFromNode: async function(node) {
			const {
				canvasApp,
				view,
			} = this;

			// Auto-fill attribute placeholders that can be auto-filled.
			const result = await svgUtil.autoFillAttributePlaceholders(this, view.getViewNodeType(), [node]);
			if (result) {
				const pageObjects = JSON.parse(result[0].ContentJson).pageObjects;
				for (const pageObject of pageObjects) {
					canvasApp.pageApi().addPageObject(pageObject, false, true);
				}
				view.updateSvgStore(view.getCanvasContent(node), true);
				view.showImpossibleNotification();
			}
		},

		/**
		 * Hides the tools pane to display the attribute menu.  For use when pressing
		 * the plus button.  Stores the currently displayed attribute so that if something
		 * else is selected the tools pane can come back.
		 */
		hideToolsPaneForAttributeMenu: function() {
			var shapeSelections = this.canvasApp.selection.selectedBaseObjects();

			this.fillingAttributeShapeId = shapeSelections[0].shapeId;

			this.updateToolbar();
		},

		/**
		 * Prepare page for copy, call canvasApp to generate new IDs for all shapes.
		 * If content is not passed then use the current content of the editor.
		 * @param {Object} content
		 * @returns {Object}
		 */
		preparePageForCopy: function(content) {
			// if content is not passed, then use current content.
			if (!content) {
				// get the current content
				content = this.get("content");
			}

			var pageJSON = JSON.parse(content.data || {pageObjects: []});
			var updatedPageJSON = this.canvasApp.selection.preparePageForCopy(pageJSON);
			content.data = JSON.stringify(updatedPageJSON);
			return content;
		},

		/**
		 * Delete selected page, but store in clipboard
		 */
		cut: function() {
			this.copy(false);
			this.view.deletePage();
		},

		/**
		 * Copy current selected page.
		 * This method is re-used in case of cut by sending false for isCopy.
		 * @param {boolean} isCopy
		 */
		copy: function(isCopy) {
			this.clipboard = {
				content: this.get("content"),
				isCopyOperation: isCopy
			};
		},

		/**
		 * Paste the copied page in clipboard
		 */
		paste: function() {
			var clipboard = this.clipboard;
			if (!clipboard) {
				this.focus();
				this.canvasApp.pageApi().paste();
				return;
			} else {
				var isComplete = this.view.addPage(this.preparePageForCopy(clipboard.content));

				// If number of pages is under max limit
				if (isComplete) {
					this._pageHtml = clipboard.content.svg;
					this.clipboard = !clipboard.isCopyOperation ? null : clipboard;
				}
			}
		},

		/**
		 * Return whether or not this is one rich-text shape
		 * @param {PageObjectType[]} pageObjects
		 */
		isRichTextPlaceholderSelected(pageObjects) {
			if (pageObjects.length === 1 && pageObjects[0].txBody?.content.includes(PLACEHOLDER_RICH_TEXT_DIV)) {
				return true;
			}
			return false;
		},

		/**
		 * Update toolbar based on current selections
		 * @param  {Object} styleFroala
		 * @param  {boolean} selectionChange
		 */
		updateToolbar: function(styleFroala, selectionChange) {
			if (!this.domNode) {
				return;
			}

			var shapeAttrs;
			var self = this;
			if (this.isDrawCanvasMode()) {
				var isTextMode = this.canvasApp.selection.isTextDrawMode();
				shapeAttrs = isTextMode ? this.canvasApp.selection.getTextShapeDefaults() : this.canvasApp.selection.getShapeDefaults();
			}

			// get updated text style before updating toolbar
			var textStyles = styleFroala || this.canvasApp.selection.getShapeTextStyles();

			// default text styles
			var defaultStyles = this.canvasApp.selection && this.canvasApp.selection.getDefaultTextStyles();
			// styleFroala is undefined  or empty means it's DRAW mode or shape is selected which has no text
			// in this case using user text style defaults
			textStyles = _.isEmpty(textStyles) ? (defaultStyles || {}) : textStyles;

			// Update shapeSelection object before updating toolbar
			this.shapeSelections = this.canvasApp.selection.selectedBaseObjects();
			if (this.shapeSelections.length == 1 && this.shapeSelections[0].shapeId == this.fillingAttributeShapeId) {
				this.shapeSelections = [];
			} else {
				this.fillingAttributeShapeId = false;
			}

			if (selectionChange) {
				const placeholderTextFormat = this.canvasApp.pageHelper.getPlaceholderTextFormat();
				if (this.isRichTextPlaceholderSelected(this.shapeSelections)) {
					if (placeholderTextFormat !== PlaceholderTextFormat.LABEL) {
						this.canvasApp.canvasApi().setPlaceholderTextFormat(PlaceholderTextFormat.LABEL);
					}
				} else if (this.previousPlaceholderTextFormat && this.previousPlaceholderTextFormat !== PlaceholderTextFormat.LABEL) {
					this.canvasApp.canvasApi().setPlaceholderTextFormat(this.previousPlaceholderTextFormat);
				}
				this.previousPlaceholderTextFormat = placeholderTextFormat;
				this.previousSelections = this.shapeSelections;
			}

			// update toolbar plugins based on selections
			_.each(this._plugins, function(w) {
				var bShow = w.canShowPlugin();
				html.setVisible(w, bShow);
				w.set("disabled", !bShow || !w.enablePlugin(self.shapeSelections, shapeAttrs, textStyles), false);
			});

			this.view.toolbarNode.enablePlugins();
		},

		/**
		 * finish initialization of the canvas
		 */
		startup: function() {
			this.inherited(arguments);
			var self = this;
			// create event handler - do so before adding plugins as they may need to listen for events
			this.eventHandler = new Events(this);

			topic.subscribe("/dnd/cancel", function() {
				self.removeHighlights();
			});

			topic.subscribe("/dnd/drop", function() {
				self.removeHighlights();
				self.focus();
			});
		},

		/**
		 * Callback on content change.
		 * This method can be called due to changes done to the page or Undo / Redo operation.
		 * In case of Undo/Redo, if there is change to list of pages (addition / deletion), then make appropriate
		 * changes. Also check for current selected page, let the view know if there is change in selected page.
		 */
		onContentChange: function(payload) {
			this._pageHtml = payload.pageHtml;
			this.isTextDirty = this.isEditTextCanvasMode() && !payload.undoable;
			if (this.isTextDirty) {
				this.rememberSelections();
			}
			var selectedPage = this.canvasApp.selection.selectedPage();
			if (this.editMode && selectedPage.id === payload.pageId) {
				this.push(null, payload.undoable);
			}
		},

		/**
		 * Callback on selection change
		 */
		onSelectionChange: function() {
			// ignore event if not in editMode.
			var selectedPage = this.canvasApp.selection.selectedPage();

			// if all pages are deleted, selectedPage will be null
			if (selectedPage) {
				this.updateToolbar(null, true);
			}
		},

		/**
		 * Callback on text selection change.
		 * @param {Array} textAttrs Text style attributes for the current text.
		 */
		onTextSelectionChange: function(textAttrs) {
			this.updateToolbar(textAttrs);

			if (this.textToReplaceEnd) {
				var start = this.textToReplaceStart;
				var end = this.textToReplaceEnd;
				this.textToReplaceStart = this.textToReplaceEnd = null;
				this.restoreTextSelection(start, end);
			}
		},

		/**
		 * Callback to hand canvas mode change.
		 * @param {string} current canvas mode.
		 */
		onModeChange: function(mode) {
			// disable text selection in Safari by setting user-select: none on body
			if (sniff("safari")) {
				document.body.style["-webkit-user-select"] = (mode === svgUtil.canvasModes.EDIT_TEXT) ? "none" : null;
			}

			this.updateToolbar();
		},

		/**
		 * Remove list of inaccesible placeholder types from canvas
		 */
		disableImpossiblePaths: function() {
			this.canvasApp.selectionApi().setImpossiblePaths([]);
		},

		/**
		 * Set list of inaccessible placeholder types on canvas using objects on canvas
		 */
		setImpossiblePaths: function() {
			var keys = this.getImpossiblePaths();

			this.canvasApp.selectionApi().setImpossiblePaths(keys);
		},

		/**
		 * Remove placeholders containing inaccessible types from all canvas pages
		 */
		removeImpossiblePaths: async function() {
			const {canvasApp, view} = this;
			const keys = this.getImpossiblePaths();
			canvasApp.pageApi().removeImpossiblePaths(keys);
			const svgStore = await view.svgStore.query();
			for (const [index, entry] of svgStore.entries()) {
				if (index === canvasApp.selection.selectedPage()) {
					return;
				}

				const pageData = JSON.parse(entry.data);
				pageData.pageObjects = canvasApp.pageHelper.removeImpossibleKeys(pageData.pageObjects, keys);
				entry.data = JSON.stringify(pageData);
			};

			await view.copyJSONtoSVGandUpdateThumbnails();
		},

		/**
		 * Return placeholders containing inaccessible types from all canvas pages
		 * @return {string[]}
		 */
		getImpossiblePaths: function() {
			const {view} = this;
			if (view.isCanvasTemplate()) {
				return [];
			}

			const {cacheView, urlRoot} = view;

			const allPlaceholderTypes = this.getAllPlaceholderTypes();

			const canvasType = view.getViewNodeType();
			const invalidRootPlaceholderTypes = allPlaceholderTypes
				.filter((type) =>
					!storeFactory.canGet(canvasType, type, urlRoot, cacheView) &&
					!storeFactory.canGetReferenceDataType(canvasType, type));

			return invalidRootPlaceholderTypes;
		},

		/**
		 * Check if drop downs get populated with selected value,
		 * in case of shape selection or default value.
		 * @param  shape {shape}
		 * @param property {String}
		 * @return {Object}
		 */
		getShapeDefaults: function(shape, property) {
			var result;
			if (shape) {
				switch (property) {
					case svgUtil.shapeProperties.arrow:
						result = {};
						result.headEnd = shape.spPr.ln && shape.spPr.ln.headEnd;
						result.tailEnd = shape.spPr.ln && shape.spPr.ln.tailEnd;
						break;
					case svgUtil.shapeProperties.strokeDash:
						result = shape.spPr.ln && shape.spPr.ln.dash || svgUtil.dashStyles[0];
						break;
					case svgUtil.shapeProperties.strokeWidth:
						result = shape.spPr.ln && shape.spPr.ln.W || svgUtil.widthStyles[1];
						break;
					case svgUtil.shapeProperties.imagePlaceholderShape:
						result = shape.spPr.geometry && shape.spPr.geometry.presetShape;
						break;
				}
			}
			return result;
		},

		/**
		 * Set shape properties.
		 * @param property {string}
		 * @param value {string}
		 */
		setShapeProperty: function(property, value) {
			switch (property) {
				case svgUtil.shapeProperties.strokeWidth:
					this.canvasApp.pageApi().setStrokeWidth(Number(value));
					break;
				case svgUtil.shapeProperties.strokeDash:
					this.canvasApp.pageApi().setLineDash(value);
					break;
				case svgUtil.shapeProperties.fillColor:
					this.canvasApp.pageApi().setObjectsFill(value);
					break;
				case svgUtil.shapeProperties.strokeColor:
					this.canvasApp.pageApi().setLineColor(value);
					break;
				case svgUtil.shapeProperties.arrow:
					this.canvasApp.pageApi().setLineCap(value.headEnd, value.tailEnd);
					break;
				case svgUtil.shapeProperties.imagePlaceholderShape:
					if (!this.canvasApp.selection.selectedObjects().length) {
						this.canvasApp.pageApi().setObjectsShape(value);
					} else {
						this.canvasApp.pageApi().changeShapeType(value);
					}
					break;
			}
			// If no shape is selected then updated the cookies
			if (!this.canvasApp.selection.selectedObjects().length) {
				this.updateStorage();
			}
		},

		getShapeProperty: function(/*String*/style, /*Shape?*/shape) {
			// summary: return current value for style
			if (!shape && this.shapeSelections) {
				shape = this.shapeSelections[0];
			}

			if (shape) {
				return svgUtil.resolve(style, shape);
			}

			return this.getShapeDefaults(shape, style);
		},

		/**
		 * Migrate old user defaults setting to new shape properties structure
		 * @param {Object} shapeProps
		 */
		migrateShapeDefaults: function(shapeProps) {
			if (!shapeProps) {
				return {};
			} else {
				var defaultShapeProps = {
					spPr: {
						fill: {
							color: shapeProps.fill,
							type: "SOLID_FILL"
						},
						ln: {
							W: shapeProps["stroke-width"],
							dash: svgUtil.dashStyles[shapeProps["stroke-dasharray"]],
							fill: {
								color: shapeProps.stroke,
							},
						},
					}
				};

				// in case of text shape props arrow is undefined.
				if (shapeProps.arrow) {
					var arrow = shapeProps.arrow;
					if (typeof arrow === "string") {
						arrow = parseInt(arrow);
					}
					defaultShapeProps.spPr.ln.headEnd = svgUtil.arrowTypes[arrow].headEnd;
					defaultShapeProps.spPr.ln.tailEnd = svgUtil.arrowTypes[arrow].tailEnd;
				}
				return defaultShapeProps;
			}
		},

		/**
		 * Migrate old user defaults setting to new properties structure
		 * @param  {Object} cs
		 */
		migrateUserDefaults: function(cs) {
			var shapeProperties = this.migrateShapeDefaults(cs.shape);
			var textShapeProperties = this.migrateShapeDefaults(cs.shapeText);
			// Convert old text style  setting to new style structure.
			var textStyles = {};

			// handle empty case
			if (cs.text) {
				textStyles = {
					color: cs.text.fill,
					"font-size": cs.text["font-size"]
				}
				var fontStyle = cs.text["font-style"];
				var fontWeight = cs.text["font-weight"];
				var textAnchor = svgUtil.oldTextStyleMap[cs.text["text-anchor"]];
				var textDecoration = cs.text["text-decoration"];
				textStyles[fontStyle] = fontStyle;
				textStyles[fontWeight] = fontWeight;
				textStyles[textAnchor] = textAnchor;
				textStyles[textDecoration] = textDecoration;
				// delete if any style value is entered as empty
				delete textStyles[""];
			}
			return {
				shapeProperties: shapeProperties,
				textShapeProperties: textShapeProperties,
				textStyles: textStyles
			}
		},

		postCreate: function() {
			this.inherited(arguments);

			this._initCanvasApp();

			this.pages = [];
			this._plugins = {};
			this._attrGetters = {};
			this._attrSetters = {};
			this.opsStack = {
				undo: [],
				redo: []
			};

			// setup note container
			this.notesNode.set({
				allowNew: true,
				allowDelete: true,
				deleteOnChange: function(item) {
					return !item.text;
				},
				deleteOnHover: true,
				disabled: true,
				disabledProperty: "readOnly",
				newButtonParams: {
					"class": "csi-link-btn",
					label: locale.resource("CanvasAddNote")
				},
				onDeleteItem: () => this._onDeleteNote(),
				widgetClass: Note,
				widgetParams: {
					canvasApp: this.canvasApp,
					onChangeNote: (item) => this._onChangeNote(item),
					view: this.view,
				},
				widgetProperties: Note.widgetProperties
			});

			// setup drop target
			this._dndTarget = new DropTarget(this.insertNode, {
				editor: this,
				enableDragDropOnMove: true,
				scrollParents: [this.view.insertNode.parentElement]
			});
		},

		/**
		 * Call canvas API to get placeholder keys and update pageModel
		 * with key references, for example when save canvas
		 * @param {Object} pageModel
		 * @return {Object}
		 */
		getCanvasContentKeys: function(pageModel) {
			return this.canvasApp.pageHelper.extractPagePlaceholderKeys(pageModel);
		},

		getCanvasNodeType: function() {
			var node = this.view.getViewNode();
			if (node.$Type !== "CanvasTemplate") {
				return node.$Type;
			} else {
				var nodeType = xml.enumGetText(node.DatasheetType);
				var rc = reflection.getClass(nodeType);
				if (!rc) {
					return "";
				} else {
					return rc.isMaster() ? rc.getRevisionClass().getType() : nodeType;
				}
			}
		},

		/**
		 * Get start types for the keys, filter out keys coming from the context, and make list of
		 * keys unique
		 * @param {string[]} keys
		 * @return {string[]}
		 */
		processPlaceholderKeys: function(keys) {
			keys = keys.map(function(key) {
				return svgUtil.getStartTypeForKey(key);
			});
			keys = _.uniq(keys);
			var nodeType = this.getCanvasNodeType();
			keys = _.without(keys, nodeType);

			return keys;
		},

		/**
		 * Return all placeholder types in page
		 * @return {string[]}
		 */
		getAllPlaceholderTypes: function() {
			var self = this;
			var pages = this.view.svgStore.data;
			var keys = [];

			_.each(this.view.svgStore.data, function(page) {
				var pageModel = JSON.parse(page.data);
				var newPageKeys = self.canvasApp.pageHelper.extractPagePlaceholderKeys(pageModel);
				_.each(newPageKeys, function(key) {
					keys.push(key);
				});
			});

			return this.processPlaceholderKeys(keys);
		},

		/**
		 * Return all non-attribute images in the page
		 * @return {string[]}
		 */
		getAllNonAttributeImages: function() {
			var pages = this.view.svgStore.data;
			var urls = [];

			_.each(this.view.svgStore.data, function(page) {
				var pageModel = JSON.parse(page.data);
				var newPageURLs = this.canvasApp.pageHelper.getAllNonAttributeImages(pageModel);
				_.each(newPageURLs, function(url) {
					urls.push(url);
				});
			}, this);

			return _.uniq(urls);
		},

		/**
		 * Return placeholder types on page
		 * @return {string[]}
		 */
		getPagePlaceholderTypes: function() {
			var keys = this.canvasApp.selection.getPagePlaceholderKeys();

			return this.processPlaceholderKeys(keys);
		},

		/**
		 * Initialize canvas app, migrate user defaults from old system to new.
		 */
		_initCanvasApp: function() {
			// create instance of canvas editor and link required listeners.
			var params = {
				editorContainer: this.canvasNode,
				editorParentContainer: this.insertNode,
				isPrimaryInstance: true,
				dontDownloadImages: false
			};

			var listeners = {
				onContentChange: lang.hitch(this, this.onContentChange),
				onSelectionChange: lang.hitch(this, this.onSelectionChange),
				onModeChange: lang.hitch(this, this.onModeChange),
				onTextSelectionChange: lang.hitch(this, this.onTextSelectionChange)
			};

			var canvasApp = this.canvasApp = svgUtil.createCanvasApp(params, listeners);

			// get user defaults in localStorage
			var oldStorage = storage.get(this.oldStorageKey);
			var newStorage = storage.get(this.storageKey);
			var userDefaults = {};

			// migrate user defaults setting
			if (oldStorage && !newStorage) {
				userDefaults = this.migrateUserDefaults(oldStorage);
			} else if (newStorage) {
				userDefaults = newStorage;
			}

			// set shape and text defaults
			canvasApp.selectionApi().setDefaultShapeProperties(userDefaults.shapeProperties);
			canvasApp.selectionApi().setTextShapeDefaultProperties(userDefaults.textShapeProperties);
			canvasApp.selectionApi().setAllTextDefaultStyles(userDefaults.textStyles);
			canvasApp.canvasApi().setFroalaLicense(sessionConstants.froalaLicense);
			canvasApp.canvasApi().setIsCanvasTemplate(this.view.isCanvasTemplate(), this.getCanvasNodeType());

			// canvas mode options enum
			svgUtil.canvasModes = canvasApp.selection.getCanvasModeOptions();
		},

		/**
		 * save defaults to localStorage if so configured
		 */
		updateStorage: function() {
			storage.set(this.storageKey, {
				shapeProperties: this.canvasApp.selection.getShapeDefaults(),
				textStyles: this.canvasApp.selection.getDefaultTextStyles(),
				textShapeProperties: this.canvasApp.selection.getTextShapeDefaults()
			});
		},

		getStorageProperty: function(property) {
			return csi.getObjectProperty(storage.get(this.storageKey), property);
		},

		/**
		 * Cleanup event handler when being destroyed
		 */
		uninitialize: function() {
			if (this.eventHandler) {
				this.eventHandler.cleanup();
			}

			this._dndTarget.destroy();
			svgUtil.removeCanvasApp(this.canvasApp);
			this.inherited(arguments);
		},

		addPlugin: function(/*String*/pluginName, /*Toolbar*/toolBarNode) {
			// summary:
			//      takes a plugin name as a string or a plugin instance and
			//      adds it to the toolbar and associates it with this editor
			//      instance.
			var plugin = PluginFactory.create(pluginName, {editor: this});
			if (plugin) {
				this._plugins[pluginName] = toolBarNode._pluginWidgets[pluginName] = plugin;
			} else if (pluginName === "|") {
				plugin = new ToolbarSeparator();
			}

			if (plugin) {
				toolBarNode.dijitToolbarNode.addChild(plugin);
				plugin.startup();
				return plugin;
			}

			console.warn("SvgCanvas: cannot find plugin ", pluginName);
			return null;
		},

		getPlugin: function(/*String*/plugin) {
			// summary: return toolbar widget by name
			return this._plugins[plugin];
		},

		/**
		 * Return true if given parameters are equal
		 * @param {CanvasContent} a
		 * @param {CanvasContent} b
		 * @return {boolean}
		 */
		compareContent: function(a, b) {
			if (!a || !b) {
				return false;
			}

			if ((a.id != b.id) || (a.split != b.split) || !_.isEqual(a.notes, b.notes)) {
				return false;
			}

			// if there is difference in JSON.
			if (a.data !== b.data) {
				return false;
			}

			return svgUtil.compareSvg(a.svg, b.svg);
		},

		edit: function() {
			this.editMode = true;
			this.splitNode.set("frozen", false);
			this.notesNode.set("disabled", false);
			this.updateToolbar();
			this._plugins.canvasrightmenu.loadOrRefreshMenu();
			this.canvasApp.canvasApi().setEditable(true);
			this.setImpossiblePaths();
			this.toggleDnD(true);
		},

		exitEditMode: function() {
			// summary: exit editor mode
			this.toggleDnD(false);

			this._resetCanvas();

			// Disable editing for canvas, it internally deselects all objects.
			this.canvasApp.canvasApi().setEditable(false);

			this.editMode = false;
			this.splitNode.set("frozen", true);
			this.notesNode.set("disabled", true);
			this.disableImpossiblePaths();
			this.updateToolbar();
		},

		/**
		 * Toggle ON/Off Drag and Drop and Paste functionality
		 * @param {boolean} active
		 */
		toggleDnD: function(bActive) {
			if (bActive) {
				if (this.editMode) {
					this.dndHTML5Toggle(true, this.insertNode, this._onDropImage, null, true, true);
				}
			} else {
				this.dndHTML5Toggle(false, this.insertNode);
			}
		},

		/**
		 * Handle dndHTML5 Image Drop event
		 * @property {File[]) files - list of dropped files
		 */
		_onDropImage: function(files) {
			this.view._files = files;
			csiAction.invoke(this.view, this.domNode, lang.mixin(new Action(), {m_id: "ImageDropCanvas", classController: SvgImageDropController}), {});
		},

		/**
		 * Set canvas content, including SVG, splitter position, and notes
		 * @param {CanvasContent} content
		 */
		_setContentAttr: function(content) {
			if (content) {
				// temporarily reset content so changes made here will not go to undo stack
				this.content = null;

				// fix for split percentage below 0 for new nodes
				if (content.split < 0) {
					content.split = DEFAULT_SPLIT_PCT;
				}

				this.splitNode.set("split", content.split);

				this.notesNode.set("store", this.view.getCanvasNotesStore(content.notes));
			}


			if (content) {
				// TODO: determine if isEditCurveCanvasMode() is still necessary here?
				svgUtil.setCanvasAppContent(this.canvasApp, content, this.isEditCurveCanvasMode());
			}

			this._set("content", content);
		},

		resize: function() {
			// summary: update width/height of canvas
			var jCanvas = $(this.canvasNode);

			this.splitNode.resize();
			this._heightPixels = jCanvas.height();
			this._widthPixels = jCanvas.width();

			// Update canvas editor dimensions based on current size
			if (this.canvasApp) {
				this.canvasApp.canvasApi().setCanvasDimension({
					editorWidth: this._widthPixels,
					editorHeight: this._heightPixels
				});
			}

			// safari on MAC wont rerender even if height and width are changed, switching display off and on will solve the issue
			jCanvas.css('display', 'none').height();
			jCanvas.css('display', 'block');
		},

		/**
		 * Add list of images (given image nodes) to canvas
		 * @param {Node[]} nodes
		 * @return {Deferred}
		 */
		addImageNodes: function(nodes) {
			var imageData = _.map(nodes, function(node) {
				return {
					alt: node.$Name,
					src: csiImage.getImageSrc(node, null, reflection.ATTR_VIEWABLE)
				};
			});
			return this.addImages(imageData, 0, 0);
		},

		/**
		 * Add list of 3d snapshots
		 * @param {Object} objects
		 * @return {Deferred}
		 */
		add3DSnapshots: function(objects) {
			const imageData = objects.map((object) => {
				const {file, img} = object;
				return {
					alt: file.name,
					src: img,
				};
			});
			return this.addImages(imageData, 0, 0);
		},

		/*
		 * Send data to canvas app to display placeholders.
		 * @param {PlaceholderData} placeholderData - Represents data used to create image and attribute place holders
		 */
		addPlaceholders: function(placeholderData) {
			this.canvasApp.pageApi().addPlaceholders(placeholderData);
		},

		addImages: function(/*Object[]*/images) {
			// summary: add images to canvas and end up with them selected; expects following attributes on image object:
			//      alt: name of image (optional)
			//      src: location of image
			var dfdResult = new Deferred();

			let mx = 0;
			let my = 0;

			// in case there are multiple images then images will have offset of 20px.
			var positionDelta = 20;

			// determine height and width of images
			var dfds = _.map(images, function(image) {
				return svgUtil.imageSize(image.src, image.alt, this.view.cacheView);
			}, this);

			all(dfds).then(
				lang.hitch(this, function(results) {
					// create SVG images and select
					var ratio = svgUtil.pixelToSvgRatio;
					var h = this.canvasNode.clientHeight;
					var w = this.canvasNode.clientWidth;
					if (h == 0) {
						h = this.view.canvasHeight;
					}
					if (w == 0) {
						w = this.view.canvasWidth;
					}
					var n = images.length;

					// reduce size of image based on number of images added
					if (n > 1) {
						var sn = Math.ceil(Math.sqrt(n));
						w /= sn;
						h /= sn;
					}

					var imageObjects = _.map(images, function(image, i) {
						var res = results[i];
						// scale image if too big for canvas
						var scale = 1;
						if ((res.w * ratio > w) || (res.h * ratio > h)) {
							scale *= Math.min(w / (res.w * ratio), h / (res.h * ratio));
						}
						var obj = {
							preserveAspectRatio: "xMinYMin meet",
							scale: ratio,
							height: res.h * scale,
							width: res.w * scale,
							x: mx,
							y: my,
							imageLink: image.src
						};

						mx += positionDelta;
						my += positionDelta;

						return obj;
					}, this);
					this.canvasApp.pageApi().addImages(imageObjects);
					dfdResult.resolve(imageObjects);
				}),
				lang.hitch(this, function(err) {
					this.onError("ERR_SVG_IMAGE_DROP", [err.message]);
					this.setMode();
				})
			);

			return dfdResult;
		},

		/**
		 * Fill placeholder with data from a type instance
		 * @param {Node} node
		 * @param {Object} placeholderTarget
		 */
		fillPlaceholderWithAttributes: function(node, placeholderTarget) {
			var view = this.view;
			var ctx = view.getContextForPlaceholders();
			ctx.throwExceptionOnUnresolvedNode = true;
			var type = node.$Type;
			var self = this;

			ctx.setNode(node);
			var keys = _.map(placeholderTarget.keys, function(key) {
				return new Key({dpkey: key});
			});
			var specs = csiCard.getAttributeCardSpecs(type, keys);
			var dfd = csiCard.getDependenciesForSpecs(ctx, specs);

			function insertAttributes() {
				const displayResults = specs.attributeSpecs.map((spec) => {
					ctx.pathUnreachable = false;

					let resultString = svgUtil.getAttributeText(ctx, spec) || "";

					if (ctx.pathUnreachable) {
						resultString = locale.resource("CanvasNoPermission");
					}

					// If we have an image with no src, restore to default view, otherwise, do not restore.
					const restoreImagePlaceholder =
						spec.format === types.FORMAT_IMAGE && !csiHeading.toImageViewable(ctx, spec.heading);

					return {
						format: spec.format,
						text: resultString,
						permission: !ctx.pathUnreachable,
						restoreImagePlaceholder,
					};
				});

				var dfds = [];
				var shapeIds = [];

				_.each(keys, function(key, index) {
					var spec = specs.attributeSpecs[index];
					if (spec.format == types.FORMAT_IMAGE) {
						ctx.pathUnreachable = false;
						var imageSrc = csiHeading.toImageViewable(ctx, spec.heading);
						if (imageSrc) {
							shapeIds.push(placeholderTarget.group.childObjects[index].shapeId);
							dfds.push(self.generateImageObject({src: imageSrc}));
						}
						if (ctx.pathUnreachable) {
							displayResults[index].text = locale.resource("CanvasNoPermission");
							displayResults[index].permission = false;
						}
					}
				});

				all(dfds).then(function(results) {
					_.each(results, function(result, index) {
						if (result) {
							self.canvasApp.pageApi().addImageToPlaceholder(result, shapeIds[index], true);
						}
					});

					self.canvasApp.pageApi().fillPlaceholderWithAttributes(displayResults, placeholderTarget.group.shapeId, node.$URL);
				});
			}

			when(dfd, function() {
				insertAttributes();
			});
		},

		/**
		 * Add the given image to the given placeholder
		 * @param {Object} image
		 * @param {Object} placeholder
		 * @param {boolean} noSelect
		 * @return {Deferred}
		 */
		insertImageAtPlaceholder: function(/*Image*/image, placeholder, noSelect) {
			// determine height and width of images
			var dfd = this.generateImageObject(image);

			return dfd.then(
				lang.hitch(this, function(res) {
					var obj = res;

					this.canvasApp.pageApi().addImageToPlaceholder(obj, placeholder.shapeId, noSelect);
					return obj;
				}),
				lang.hitch(this, function(err) {
					this.onError("ERR_SVG_IMAGE_DROP", [err.message]);
					this.setMode();
				})
			);
		},

		/**
		 * Returns a deferred that will generate an object for an image, used to insert into a placeholder
		 * @param {Object} image
		 * @return {Deferred}
		 */
		generateImageObject: function(image) {
			var ratio = svgUtil.pixelToSvgRatio;
			var h = this.canvasNode.clientHeight;
			var w = this.canvasNode.clientWidth;

			// determine height and width of images
			var dfd = svgUtil.imageSize(image.src, image.alt, this.view.cacheView);

			return dfd.then(
				lang.hitch(this, function(res) {
					var obj = {
						preserveAspectRatio: "xMinYMin meet",
						scale: ratio,
						height: res.h,
						width: res.w,
						// Don't need x and y because we will use the position where the image is added
						x: 0,
						y: 0,
						imageLink: image.src
					};

					return obj;
				})
			);
		},

		/**
		 * Function to lock / unlock selected shape.
		 * @param {boolean} true to lock shape or false to unlock.
		 */
		lockShapes: function(lock) {
			this.canvasApp.pageApi().lockObjects(lock);
		},

		/**
		 * Set format painter true
		 */
		formatPainter: function() {
			this.canvasApp.canvasApi().setFormatPainter(true);
		},

		/**
		 * Delete selected shapes.
		 */
		deleteShapes: function() {
			const oldSelections = this.getShapeSelections();
			this.rememberSelections(oldSelections);
			if (oldSelections.length) {
				this.canvasApp.pageApi().deleteObjects();
			}
		},

		/**
		 * Enable / Disable snap to guides.
		 * @param {boolean} bEnable
		 */
		setSnapToGuides: function(bEnable) {
			this.canvasApp.canvasApi().setSnapToGuide(bEnable);
		},

		/**
		 * Set text style
		 * @param {string} style to be changed
		 * @param {string} value for the style.
		 */
		setTextStyle: function(style, value, isApply) {
			// if link popup is already open, close it and do nothing
			if (style === "link" && typeof window.getSelection != "undefined" &&
				window.getSelection().anchorNode == null) {
				return;
			}

			this.rememberTextHighlight();

			this.canvasApp.pageApi().setTextStyle(style, value, isApply);
			// If no shape is selected then updated the cookies
			if (!this.canvasApp.selection.selectedObjects().length) {
				this.updateStorage();
			}
		},

		/**
		 * Enable / Disable snap to grid.
		 * @param {boolean} bEnable
		 */
		setSnapToGrid: function(bEnable) {
			this.canvasApp.canvasApi().setSnapToGrid(bEnable);
		},

		/**
		 * Push selected shapes to front or back
		 * @param {boolean} bBack
		 */
		changeSortOrder: function(bBack) {
			this.canvasApp.pageApi().changePageObjectOrder(!bBack);
		},

		/**
		 * Distribute objects equally.
		 * @param {boolean} bHorizontal Distribute horizontally if passed true, else distribute vertically.
		 */
		distributeObjects: function(bHorizontal) {
			this.canvasApp.pageApi().distributeObjects(bHorizontal);
		},


		/**
		 * Push canvas changes
		 * @param {string} ops - "new", "dnd", or null (standard) operation
		 * @param {boolean} isUndoable - is this operation undoable
		 */
		push: function(ops, isUndoable) {
			// summary: store SVG in undo stack
			if (!this.content) {
				return;
			}

			const params = this.get("content");
			params.ops = ops;
			params.sortList = this.view.getVisibleNodes();

			// if we have remembered selections, store and forget them
			if (isUndoable && this.rememberedSelections) {
				params.selected = this.rememberedSelections;
				this.rememberedSelections = null;
			} else {
				params.selected = this.getShapeSelections();
			}

			// if we have remembered text highlights, store and forget them
			if (isUndoable && this.textSelectionEnd) {
				params.textSelectionStart = this.textSelectionStart;
				params.textSelectionEnd = this.textSelectionEnd;
				this.textSelectionStart = this.textSelectionEnd = null;
			}

			// set generated HTML to content, rest of the logic uses svg to update thumbnail.
			this.content.svg = params.svg;

			if (ops !== "dnd") {
				// don't push duplicate contents to stack
				if (this.compareContent(params, _.last(this.opsStack.undo))) {
					return;
				}

				this.view.updateSvgStore(params, true);
			}

			if (isUndoable) {
				this.pushToUndoStack(params);
			}

			this.onChange();
			this.setImpossiblePaths();
		},

		/**
		 * Push canvas content to undo stack
		 * @param {object} content
		 */
		pushToUndoStack: function(content) {
			const {opsStack, originalContent} = this;
			// keep stack at no more than 50 operations
			if (opsStack.undo.length == 50) {
				const oldContent = opsStack.undo.shift();
				originalContent[oldContent.id] = oldContent;
				this.originalSortList = oldContent.sortList;
			}

			opsStack.undo.push(content);
			opsStack.redo = [];

			// update undo / redo icons
			this.updateToolbar();
		},

		/**
		 * Stores the indices of the currently highlighted text
		 */
		rememberTextHighlight: function() {
			var froala = this.canvasApp.selection.getFroalaInstance();
			if (froala && froala.$el) {
				var element = froala.$el[0];

				var start = 0, end = 0;
				var sel, range, priorRange;
				if (typeof window.getSelection != "undefined" &&
					window.getSelection().anchorNode != null) {
					range = window.getSelection().getRangeAt(0);
					priorRange = range.cloneRange();
					priorRange.selectNodeContents(element);
					priorRange.setEnd(range.startContainer, range.startOffset);
					start = priorRange.toString().length;
					end = start + range.toString().length;
				} else if (typeof document.selection != "undefined" &&
					(sel = document.selection).type != "Control") {
					// internet explorer
					range = sel.createRange();
					priorRange = document.body.createTextRange();
					priorRange.moveToElementText(element);
					priorRange.setEndPoint("EndToStart", range);
					start = priorRange.text.length;
					end = start + range.text.length;
				}

				this.textSelectionStart = start;
				this.textSelectionEnd = end;
			}
		},

		/**
		 * Stores the currently selected shapes.  It is necessary to call this
		 * before operations such as delete, that prevent their ID's from being
		 * stored later.
		 * @param {Object[]} oldSelections - if passed in, use these instead of calculating anew
		 */
		rememberSelections: function(oldSelections) {
			this.rememberedSelections = oldSelections ?? this.getShapeSelections();
		},

		/**
		 * Select shapes with given ids
		 * @param {string[]} ids
		 */
		restoreShapeSelections: function(ids) {
			_.each(ids, function(id) {
				this.canvasApp.selectionApi().selectObjectById(id, true, false);
			}, this);
		},



		/**
		 * Given an offset number of characters and parent element, returns the correct element
		 * and character offset within that element for selection
		 * @param {Object} element
		 * @param {number} offset
		 * @returns {SelectionTarget}
		 */
		findRangeInputsByOffset: function(element, offset) {
			var textNodes = getConsecutiveTextNodes(element);

			var result = {
				element: null,
				offset: 0
			};

			for (i = 0; i < textNodes.length; i++) {
				if (offset < textNodes[i][0].length) {
					result.element = textNodes[i][0];
					result.offset = offset;

					return result;
				} else {
					offset = offset - textNodes[i][0].length;
				}
			}

			result.element = textNodes[textNodes.length - 1][0];
			result.offset = result.element.length;

			return result;
		},

		/**
		 * Reselect the text that was selected before operation
		 * @param {number} start
		 * @param {number} end
		 */
		restoreTextSelection: function(start, end) {
			var froala = this.canvasApp.selection.getFroalaInstance();
			if (froala && froala.$el) {
				var element = froala.$el[0];
				if (start > froala.$el.text().length) {
					return;
				}
				else if (end > froala.$el.text().length) {
					end = froala.$el.text().length;
				}

				var startResult = this.findRangeInputsByOffset(froala.$el, start);
				var endResult = this.findRangeInputsByOffset(froala.$el, end);

				if (window.getSelection && document.createRange) {
					var sel = window.getSelection();
					var range = document.createRange();
					range.setStart(startResult.element, startResult.offset);
					range.setEnd(endResult.element, endResult.offset);
					sel.removeAllRanges();
					sel.addRange(range);
				} else if (document.selection && document.body.createTextRange) {
					// internet explorer
					var textRange = document.body.createTextRange();
					textRange.moveToElementText(element);
					textRange.moveStart("character", start);
					textRange.moveEnd("character", end);
					textRange.select();
				}
			}
		},

		/**
		 * Stores the ID's from a list of shapes in a given array. When it encounters groups,
		 * stores all base-level shape ID's in the group.
		 * @param {string[]} array
		 * @param {object[]} shapes
		 */
		storeIdsInArray: function(array, shapes) {
			_.each(shapes, function(s) {
				if (s.type === "GROUP_SHAPE") {
					this.storeIdsInArray(array, s.childObjects);
				} else {
					array.push(s.shapeId);
				}
			}, this);
		},

		/**
		 * Return a list of the currently selected shapes' ids
		 * @return {string[]}
		 */
		getShapeSelections: function() {
			var ids = [];

			if (this.shapeSelections) {
				this.storeIdsInArray(ids, this.shapeSelections);
			}

			return ids;
		},

		/**
		 * Apply undo/redo
		 * @param {string} opsType
		 */
		_do: function(opsType) {
			if (this.isEditTextCanvasMode()) {
				this.cancelTextEdit();
			}

			var stack = this.opsStack[opsType];
			if (!stack.length) return;

			var inverseStack = this.opsStack[opsType == "undo" ? "redo" : "undo"];
			var data = stack.pop();

			inverseStack.push(data);

			this.view.switchToStackView(data, opsType);

			this.restoreShapeSelections(data.selected);

			if (data.textSelectionEnd) {
				this.textToReplaceStart = data.textSelectionStart;
				this.textToReplaceEnd = data.textSelectionEnd;
				this.canvasApp.canvasApi().editPageObject();
			}

			this.updateToolbar();
			this.onChange();
		},

		undo: function() {
			// summary: apply SVG as of last operation, saving current SVG to redo stack; only allowed when in Select mode
			if (this.isUndoAllowed()) {
				this._do("undo");
			}
		},

		redo: function() {
			// summary: apply SVG of last undone operation, saving current SVG to undo stack; only allowed when in Select mode
			if (this.isRedoAllowed()) {
				this._do("redo");
			}
		},

		_resetCanvas: function() {
			// summary: reset canvas contents to first step
			this.clipboard = null;
			this.opsStack.undo = [];
			this.opsStack.redo = [];
		},

		/**
		 * When split percentage changes, resize SVG canvas
		 * @param {number} value
		 */
		_onChangeSplit: function(value, dragging) {
			// resize canvas based on new splitter position
			this.resize();

			if (this.editMode && !dragging) {
				this.push(null, true);
			}
		},

		/**
		 * When splitter clicked, deselect all objects
		 */
		_onStartDrag: function() {
			this.deselectAllShapes();
		},

		/**
		 * Push change onto stack when note edited. The store should already be updated with change.
		 * @param {CanvasNote} item
		 */
		_onChangeNote: function(item) {
			var node = this.view._createClientNode("Comment");
			var url = node.$URL;

			// store original url of this comment if coming from existing node
			var mapUrls = this.view.mapSavedUrls;
			var originalUrl = item.url;

			if (xml.isClientUrl(originalUrl)) {
				originalUrl = mapUrls[originalUrl];
			}

			mapUrls[url] = originalUrl;

			// update URL of item to new node
			item.url = url;

			// store all persistent data on cached node
			xml.nodeCreateClientAttr(node, "Author", xml.ATTR_REF, sessionConstants.userUrl);
			xml.nodeCreateClientAttr(node, "Comment", xml.ATTR_STRING, item.text);
			xml.nodeCreateClientAttr(node, "Date", xml.ATTR_TIME, item.time);

			this.push(null, true);
		},

		/**
		 * Push change when note deleted.
		 */
		_onDeleteNote: function() {
			this.deselectAllShapes();
			this.push(null, true);
		},

		/**
		 * Create item for new comment - no need to create client node yet until text has been entered
		 * @return {CanvasNote}
		 */
		_onNewNote: function() {
			return {
				id: _.uniqueId(),
				author: sessionStore.currentUser.$Name,
				text: "",
				time: 0
			};
		},

		/**
		 * Start editing note after node is loaded
		 * @param {CanvasNote} item
		 * @param {Note} widget
		 */
		_onRenderNewNote: function(item, widget) {
			const observer = new MutationObserver((mutations) => {
				if (mutations[0].addedNodes && mutations[0].addedNodes.length) {
					widget._editor.froalaEditor.el.focus();
				}
				observer.disconnect();
			});

			observer.observe(widget._editor.froalaEditor.el, {childList: true});
		},

		/**
		 * Return undo stack has entry or not.
		 * @return {boolean}
		 */
		hasUndoStack: function() {
			// summary: indicate if there are operations to undo
			return !!this.opsStack.undo.length;
		},

		/**
		 * Return redo stack has entry or not.
		 * @return {boolean}
		 */
		hasRedoStack: function() {
			return !!this.opsStack.redo.length;
		},

		/**
		 * Return true if there are unsaved changes to canvas
		 */
		isCanvasDirty: function() {
			return this.isTextDirty || this.hasUndoStack() || this.hasRedoStack() || this.placeholdersModified;
		},

		/**
		 * Return if Undo is enabled or not.
		 * Undo is enabled only if
		 * There are Undo operations.
		 * Not in Text edit, Curve edit and in Draw mode.
		 * If there is only one operation and it is for client URL.
		 * @return {boolean}
		 */
		isUndoAllowed: function() {
			return this.hasUndoStack() && !this.isDrawCanvasMode();
		},

		/**
		 * Return if Redo allowed.
		 * Redo is allowed only if
		 * There are Redo operations.
		 * Not in Text edit, Curve edit and in Draw mode.
		 */
		isRedoAllowed: function() {
			return this.hasRedoStack() && !this.isDrawCanvasMode();
		},

		/**
		 * Cut selected shapes
		 */
		cutSelection: function() {
			this.rememberSelections();
			this.canvasApp.clipboardApi().cut();
		},

		/**
		 * Group selected shapes
		 */
		groupSelection: function() {
			this.rememberSelections();
			this.canvasApp.pageApi().group();
		},

		/**
		 * Ungroup selected shapes
		 */
		ungroupSelection: function() {
			this.rememberSelections();
			this.canvasApp.pageApi().ungroup();
		},

		/**
		 * Get primary selected object
		 * @return {ShapeProperties}
		 */
		getPrimarySelectedShape: function() {
			return this.canvasApp.selection.selectedPrimaryObject();
		},

		/**
		 * Returns flag from c8-canvas library wether format painter is active or not
		 * @return {boolean}
		 */
		isFormatPainterSelected: function() {
			return this.canvasApp.selection.isFormatPainterSelected();
		},

		/**
		 * Redirect focus to view
		 * @param focusFroala {Boolean} Flag to indicate whether to focus froala or not
		 */
		focus: function(focusFroala) {
			// if mode is not EDIT_TEXT, after any toolbar operation focusing the focusNode
			// if mode is EDIT_TEXT , focusing the active froala instance so after completing operation on toolbar
			// focus shift's back to text editor.
			if (!this.isEditTextCanvasMode()) {
				this.view.focus();
			} else if (focusFroala) {
				this.canvasApp.selection.focusFroalaInstanceIfExists();
			}
		},

		/**
		 * Store SVG for the specified page URL.
		 * @param {string} pageUrl Page node URL
		 * @param {string} svgString Page SVG string
		 */
		_cachePageSvg: function(pageUrl, svgString) {
			var pageIndex = this._findPageIndex(pageUrl);
			this.pages[pageIndex].svg = svgString;
		},

		/**
		 * Return object with canvas content
		 * @return {CanvasContent}
		 */
		_getContentAttr: function() {
			var selectedPage = this.canvasApp.selection.selectedPage();
			if (!selectedPage) {
				return null;
			}

			var split = this.splitNode.get("split");
			var notes = this._getNotes();
			var pageJSON = lang.clone(selectedPage);
			var data = JSON.stringify(pageJSON);
			var canvasHTML = "";

			if (pageJSON && pageJSON.pageObjects.length) {
				canvasHTML = svgUtil.svgContainerClean(this._pageHtml);
			}

			return {
				id: this.content ? this.content.id : null,
				notes: notes,
				split: split,
				svg: canvasHTML,
				data: data
			};
		},

		/**
		 * Return list of note ids for current page
		 * @return {string[]}
		 */
		_getNotes: function() {
			var store = this.notesNode.store;
			if (store) {
				return _.pluck(this.notesNode.store.query(), "url");
			}

			return [];
		},

		/**
		 * Return whether or not canvas mode is currently the default.
		 * @return {boolean}
		 */
		isDefaultCanvasMode: function() {
			return this.canvasApp.pageHelper.isDefaultCanvasMode();
		},

		/**
		 * Return whether or not currently set to draw a placeholder.
		 * @return {boolean}
		 */
		isDrawPlaceholder: function() {
			return this.canvasApp.selection.isDrawPlaceholder();
		},

		/**
		 * Return whether or not canvas mode is currently DRAW.
		 * @return {boolean}
		 */
		isDrawCanvasMode: function() {
			return this.canvasApp.pageHelper.isDrawCanvasMode();
		},

		/**
		 * Return whether or not canvas mode is currently EDIT_CURVE.
		 * @return {boolean}
		 */
		isEditCurveCanvasMode: function() {
			return this.canvasApp.pageHelper.isEditCurveCanvasMode();
		},

		isEditPenMode: function() {
			return this.canvasApp.pageHelper.isEditPenMode();
		},

		/**
		 * Return whether or not canvas mode is currently EDIT_PICTURE.
		 * @return {boolean}
		 */
		isEditPictureCanvasMode: function() {
			return this.canvasApp.pageHelper.isEditPictureCanvasMode();
		},

		/**
		 * Return whether or not canvas mode is currently EDIT_TEXT.
		 * @return {boolean}
		 */
		isEditTextCanvasMode: function() {
			return this.canvasApp.pageHelper.isEditTextCanvasMode();
		},

		/**
		 * Return whether or not canvas mode is currently NODRAW.
		 * @return {boolean}
		 */
		isNoDrawCanvasMode: function() {
			return this.canvasApp.pageHelper.isNoDrawCanvasMode();
		},

		/**
		 * Return whether or not canvas mode is currently SELECT.
		 * @return {boolean}
		 */
		isSelectCanvasMode: function() {
			return this.canvasApp.pageHelper.isSelectCanvasMode();
		},

		/**
		 * Return if current selected mode is draw mode or not
		 * @param  {string} shapeType
		 * @return {boolean}
		 */
		isDrawMode: function(shapeType) {
			var canvasData = this.canvasData;
			var isCurrentCanvasData = false;
			switch (shapeType) {
				case "pen":
					isCurrentCanvasData = (canvasData.shape === shapeType);
					break;
				case "line":
					// in line mode mode.shape is line
					isCurrentCanvasData = (canvasData.shape === shapeType);
					break;
				case "curve":
					// in curve mode mode.shape is undefined
					isCurrentCanvasData = !canvasData.shape;
					break;
				case "text":
					// in text mode mode.isTextShape is true
					isCurrentCanvasData = canvasData.isTextShape;
					break;
				case "shapeSelect":
				case "imagePlaceholder":
					// in shape select mode or imagePlaceholder mode, mode.shape is
					// actual shape selected from shape palette and isTextShape false
					isCurrentCanvasData = canvasData.shape && (canvasData.shape !== "line") && (canvasData.shape !== "pen") && !canvasData.isTextShape;
					break;
			}
			return isCurrentCanvasData && this.isDrawCanvasMode();
		},

		/**
		 * Return whether selected mode is crop mode or not
		 * @return {boolean}
		 */
		isCropMode: function() {
			return this.isEditPictureCanvasMode();
		},

		/**
		 * Cropping an image and also editing points on that image are both still EDIT_PICTURE mode,
		 * so this assures we are in crop mode, but we are not editing custom points inside of the crop.
		 * @return {boolean}
		 */
		isCropPictureMode: function() {
			return this.isEditPictureCanvasMode() && !this.isEditCustomCrop();
		},

		/**
		 * Return whether edits are allowed or not.
		 * @return {boolean}
		 */
		isEditMode: function() {
			return this.editMode;
		},

		/**
		 * Return whether user is editing a custom curve
		 * @return {boolean}
		 */
		isEditCustomCurve: function() {
			return (this.isEditCurveCanvasMode() || this.isEditPictureCanvasMode()) &&
				this.canvasApp.selection.isSelectedShapeCustom();
		},

		/**
		 * Return whether user is editing a custom curve and it has at least one point selected
		 * @return {boolean}
		 */
		isEditCustomCurveAndHasPoints: function() {
			return (this.isEditCurveCanvasMode() || this.isEditPictureCanvasMode()) &&
				this.canvasApp.selection.isSelectedShapeCustom() && this.canvasApp.selection.arePointsSelected();
		},

		/**
		 * Return whether selected mode is edit curve mode or not
		 * @return {boolean}
		 */
		isEditCustomCrop: function() {
			return this.isEditPictureCanvasMode() &&
				this.canvasApp.selection.isSelectedShapeCustom();
		},

		/**
		 * Return whether the canvas app currently has a subgroup element selected
		 * @return {boolean}
		 */
		isSubgroupSelected: function() {
			return this.canvasApp.selection.isSubgroupSelected();
		},

		editPointsPageObject: function() {
			this.canvasApp.canvasApi().editPageObject(true);
		},

		editPageObject: function() {
			this.canvasApp.canvasApi().editPageObject();
		},

		/**
		 * Set draw mode to NODRAW
		 */
		setNoDrawMode: function() {
			// update the toolbar to reflect NODRAW mode
			this.updateToolbar();

			// second argument preserves current selection, in case of cancel
			this.canvasApp.canvasApi().setCanvasMode(svgUtil.canvasModes.NODRAW, true);
		},

		/**
		 * Set draw mode and shape draw tool
		 * @param shapeType {string} Shape type that need to draw
		 * @param editOnComplete {boolean} This flag is used to decide whether or not make shape editable on draw complete
		 * @param isTextShape {boolean} Used to tell it's text shape or not
		 * @param isTableShape {boolean} Used to tell it's table shape or not
		 */
		setDrawMode: function(shapeType, editOnComplete, isTextShape, isTableShape, isPlaceholder) {
			this.canvasData = {
				shape: shapeType,
				isTextShape: isTextShape,
				isPlaceholder: isPlaceholder
			};
			this.canvasApp.canvasApi().setCanvasMode(svgUtil.canvasModes.DRAW);
			this.canvasApp.selectionApi().setShapeDrawTool(shapeType, editOnComplete, isTextShape, isTableShape, isPlaceholder);
		},

		/**
		 * Set shape draw tool metadata
		 * @param metadata {Object} Metadata for shape, for example if text table, rows and columns
		 */
		setDrawMetadata: function(metadata) {
			this.canvasApp.selectionApi().setShapeDrawMetadata(metadata);
		},

		/**
		 * Deselect all shapes
		 */
		deselectAllShapes: function() {
			this.canvasApp.selectionApi().deselectAllObjects();
		},

		/**
		 * Remove all highlights
		 */
		removeHighlights: function() {
			const {
				canvasApp,
				insertNode,
			} = this;
			if (insertNode) {
				insertNode.classList.remove("csi-canvas-drag-over-inactive");
			}

			canvasApp.selectionApi().resetHighlight();
		},

		/**
		 * Deselect all shapes.  If text is being edited, cancel the changes
		 */
		cancelTextEdit: function() {
			this.textSelectionStart = this.TextSelectionEnd = null;
			this.canvasApp.selectionApi().deselectAllObjects(true);
		},

		/**
		 * Switch mode - set to false during long operations to disable all functionality
		 * @param  {String|Object} [mode]
		 * @param  {Deferred} [dfd]
		 */
		setMode: function(mode, dfd) {
			if (mode === false) {
				mode = false;
			} else if (!mode || (typeof mode === "string")) {
				mode = this.getPlugin(mode || this.canvasApp.pageHelper.getDefaultCanvasMode());
			}

			if (mode != this.mode) {
				if (this.mode) this.mode.exitMode();
				this.mode = mode;
				if (this.mode) this.mode.enterMode(dfd);
				this.updateToolbar();
			}
		},

		/**
		 * Forward vertical value to SplitPane
		 * @param {boolean} value
		 */
		_setVerticalAttr: function(value) {
			this._set("vertical", value);
			this.splitNode.set("vertical", value);
			this.resize();
		},

		/**
		 * Forward zoom value to SplitPane
		 * @param {number} value
		 */
		_setZoomAttr: function(value) {
			this._set("zoom", value);
			this.splitNode.set("zoom", value);

			// Set zoom factor in c8-canvas library
			if (this.canvasApp) {
				this.canvasApp.canvasApi().setCanvasZoom(value);
			}

			// set zoom on SVG util and resize so edit controls will have proper size
			svgUtil.zoomFactor = value;
			this.resize();
		},

		destroy: function() {
			this.dndHTML5Destroy();
			this.inherited(arguments);
		},

		/**
		 * @return true if selected shape is an attribute placeholder
		 */
		isSelectedShapeAttributePlaceholder: function() {
			return this.canvasApp.selection.isSelectedShapeAttributePlaceholder();
		},

		/**
		 * @return true if selected shape is image placeholder or an image made from a placeholder
		 */
		isSelectedShapeFromPlaceholder: function() {
			return this.canvasApp.selection.isSelectedShapeFromPlaceholder();
		},


		/**
		 * @return true if selected shape is image placeholder or child objects contain an image placeholder
		 */
		isSelectedShapeImagePlaceholder: function() {
			return this.canvasApp.selection.isSelectedShapeImagePlaceholder();
		},

		/**
		 * @return true if selected shape supports edit points
		 */
		isSelectedShapesSupportEditPoints: function() {
			return this.canvasApp.selection.isSelectedShapesSupportEditPoints();
		},

		/**
		 * @return true if all selected shapes are made from or are image placeholders
		 */
		allSelectedObjectsAreFromImagePlaceholders: function() {
			return this.canvasApp.selection.allSelectedObjectsAreFromImagePlaceholders();
		},

		/**
		 * @return true if all selected shapes are image placeholders
		 */
		allSelectedObjectsAreImagePlaceholders: function() {
			return this.canvasApp.selection.allSelectedObjectsAreImagePlaceholders();
		},

		/**
		 * @param {Object[]} shapes - all shapes to be checked
		 * @param {boolean} forceEdited - also require all images to have been edited
		 * @return {boolean} true if all selected shapes are images
		 */
		shapesArePictures: function(shapes, forceEdited) {
			if (!shapes.length) {
				return false;
			}
			const {selection} = this.canvasApp;

			if (shapes.some(shape => !selection.isPicture(shape))) {
				return false;
			}

			if (forceEdited && shapes.some(shape => !shape.hasBeenEdited)) {
				return false;
			}

			return true;
		},

		/**
		 * scales image, requires original size
		 * @param {string} scaleType - original, reset, fit, fill
		 */
		scaleImageToFrame: function(scaleType) {
			let imageLinks = this.canvasApp.selection.getSelectedImageLinks();

			let self = this;
			let dfds = [];

			_.each(imageLinks, (imageLink) => {
				dfds.push(svgUtil.imageSize(imageLink, null, self.view.cacheView));
			});

			all(dfds).then(function(results) {
				self.canvasApp.pageApi().scaleImageToFrame(scaleType, results);
			});
		},

		/**
		 * Return placeholder data, with differences, and placeholders to be deleted.
		 * @param {Object[]} pagesData - array of page objects
		 * @returns {Promise<void>}
		 */
		_checkPlaceholderValues: function(pagesData) {
			var self = this;
			var view;
			var ctx;
			var placeholderData = [];
			var flatPlaceholderData = [];

			// Reset these before checking for differences.
			self.placeholderDifferenceData = null;
			self.placeholderDeletions = [];
			self.placeholderDataChanged = false;

			if (pagesData && pagesData.length) {
				view = this.view;
				ctx = view.getContextForPlaceholders();

				// Top-level of differences data structure is array of page differences data
				_.each(pagesData, function(page) {
					if (page.data) {
						// Get pages content to use in extracting shape ids and placeholder data.
						var pageData = JSON.parse(page.data);

						// Get placeholder data from the c8-canvas app, to be compared with latest data values.
						// Data is organized by shape id of the group containing the placeholder.
						var pagePlaceholderObjectData = self.canvasApp.pageHelper.getAllPagePlaceholderObjectData(pageData);

						placeholderData.push(pagePlaceholderObjectData);
					}
				});

				// Flatten placeholder data for efficiency with some operations.
				flatPlaceholderData = svgUtil.flattenPlaceholderData(placeholderData);

				// Add a flag to placeholder attributes that should be deleted. Used when presenting differences
				// in wizard to user, indicating attributes that will be removed.
				svgUtil.addDeletePlaceholderFlag(flatPlaceholderData);

				// Ensure nodes populating placeholders are in cache.
				var nodesDfd = svgUtil.getPlaceholderDependencies(ctx, flatPlaceholderData, [view.urlRoot]);

				// Executed asynchronously when dependencies have been loaded in the cache.
				return when(nodesDfd, function() {
					// Current view node type for use with autofill placeholders.
					var currentViewNodeType = view.getViewNodeType();

					// Get the latest placeholder values to be compared with those stored with placeholders.
					// This needs to be done for each placeholder object that has been filled.
					_.forEach(flatPlaceholderData, function(placeholder) {
						placeholder.type = svgUtil.getStartTypeForKey(placeholder.attributeKey);

						// Set nodeURL if placeholder is autofill.
						if (placeholder.type === currentViewNodeType) {
							placeholder.nodeURL = view.urlRoot;
						}

						var nodeID = placeholder.nodeURL;
						placeholder.originalValue = placeholder.value;
						placeholder.latestValue = null;
						placeholder.url = nodeID;
						placeholder.changed = false;
						placeholder.noCurrentAccess = false;

						if (nodeID || placeholder.deletePlaceholder) {
							ctx.pathUnreachable = false;
							const latestValueData = svgUtil.getPlaceholderValue(ctx, placeholder, nodeID);

							if (ctx.pathUnreachable) {
								placeholder.noCurrentAccess = true;
							}

							// If attribute is invalid, show message stating attribute will be removed, as we can't get a new value.
							placeholder.latestValue = placeholder.deletePlaceholder ? locale.resource("CanvasDeletePlaceholder") : latestValueData.value;

							placeholder.name = latestValueData.name;
							placeholder.isImage = latestValueData.isImage;

							var latestValueCompare = (placeholder.latestValue == null) ? "" : placeholder.latestValue.toString();
							var originalValueCompare = (placeholder.originalValue == null) ? "" : placeholder.originalValue.toString();

							// does node still exist?
							const nodeExists = ctx.cache.getNode(nodeID);

							// If we have a nodeID but it is not in the cache, it was deleted, and we need to restore default value.
							// If we have an image that was previously filled but is no longer filled, restore it as well.
							if (!nodeExists || (placeholder.isImage && originalValueCompare && !latestValueCompare)) {
								placeholder.restoreToDefaultView = true;
								placeholder.saveURL = !!nodeExists ? true : false;
							}

							if (placeholder.isImage && !placeholder.restoreToDefaultView && !placeholder.noCurrentAccess) {
								// If image url does not have URL query parameter then it is likely the type instance does not
								// have an image associated with a label, which is not a difference we want to detect.
								var originalValueIsUrl = svgUtil.hasURLQueryParameter(placeholder.originalValue);
								var latestValueIsUrl = svgUtil.hasURLQueryParameter(placeholder.latestValue);

								// TODO shouldn't have to do this replace. Need fix so image data can be compared to detect differences.
								// We reduce the values to just the node id as other parts of the url differ when really no differences.
								originalValueCompare = originalValueIsUrl ? placeholder.originalValue : "";
								latestValueCompare = latestValueIsUrl ? placeholder.latestValue : "";

								// We reduce the values to just the node id as other parts of the url differ when really no differences.
								const removeUnnecessaryImageData = (imageSrc) => {
									imageSrc = imageSrc.replace(/.*?[\?\&]URL=(.*?)&.*/, "$1");

									return imageSrc;
								}

								originalValueCompare = removeUnnecessaryImageData(originalValueCompare);
								latestValueCompare = removeUnnecessaryImageData(latestValueCompare);

								if (originalValueCompare == "Default") {
									originalValueCompare = "";
								}

								// For images we need to pass image dimensions and an image data object.
								var obj = {
									preserveAspectRatio: "xMinYMin meet",
									scale: svgUtil.pixelToSvgRatio,
									height: latestValueData.imageDimensions.h,
									width: latestValueData.imageDimensions.w,
									// Don't need x and y because we will use the position where the image is added
									x: 0,
									y: 0,
									imageLink: placeholder.latestValue
								};

								placeholder.imageObj = obj;
							}

							// We will ignore placeholders whose paths could not be completed
							if (!placeholder.noPermissionToFill && !placeholder.noCurrentAccess) {
								if (placeholder.restoreToDefaultView || (latestValueCompare !== originalValueCompare)) {
									placeholder.changed = true;
									// Flag canvas page as having changed attributes.
									self.placeholderDataChanged = true;
								}
							}
						}
					});

					svgUtil.addPlaceholderIds(placeholderData);

					self.updateAccessibleNodes(placeholderData);

					// Filter placeholder data for only attributes that changed or that will be deleted.
					// For changed attributes, need to keep all data for the placeholder group containing the changed attributes.
					placeholderData = svgUtil.filterAttributesForChangeOrDeletion(placeholderData, self.placeholderDeletions);

					// Store data on placeholder attribute values for update and deletion.
					self.placeholderDifferenceData = placeholderData;
				});
			}
		},

		/**
		 * Fill all placeholders on all pages that are accessible now but were not accessible
		 * when created.
		 * @param {Object[]} placeholderData - the placeholder data to use for replacements
		 */
		updateAccessibleNodes: function(placeholderData) {
			// Loop through all pages in svgStore.
			_.each(this.view.svgStore.data, function(data, index) {
				var pageData = JSON.parse(data.data);

				var flatData = svgUtil.flattenPlaceholderData(placeholderData[index]);
				flatData = _.filter(flatData, function(placeholder) {
					return !placeholder.deletePlaceholder && placeholder.noPermissionToFill && !placeholder.noCurrentAccess;
				});

				pageData.pageObjects = this.canvasApp.pageHelper.updatePageObjects(index, pageData.pageObjects, flatData, [], true);

				// Update the page in the svgStore.
				this.view.svgStore.data[index].data = JSON.stringify(pageData);
				svgUtil.setCanvasAppContent(this.canvasApp, this.view.svgStore.data[0]);
			}, this);
		},

		/**
		 * Update all placeholders on all canvas pages.
		 * Use canvas updatePageObjects API to update pageObjects inside JSON, then use updateThumbnailFromJSON
		 * to update the svg, svgStore, and thumbnail asynchronously from the new JSON.
		 */
		updateAllAttributePlaceholders: async function() {
			const {canvasApp, view} = this;

			// If set to true canvas won't save.
			view.bInitCanvas = false;

			// This allows us to mark canvas as dirty.
			this.placeholdersModified = true;

			// Update JSON
			const svgStore = await view.svgStore.query();
			for (const [page, entry] of svgStore.entries()) {
				const pageData = JSON.parse(entry.data);

				// Only send difference data for the current page.
				const flatData = svgUtil.flattenPlaceholderData(this.placeholderDifferenceData[page]);

				if (flatData.length) {
					pageData.pageObjects = canvasApp.pageHelper.updatePageObjects(page, pageData.pageObjects, flatData, this.placeholderDeletions);
					entry.data = JSON.stringify(pageData);
					view.svgStore.put(entry);
				}
			};

			// Now save the canvas.
			view.save(true, true);

			// Check for differences again. If none (likely because we just accepted updates) then hide review updates button.
			await this._checkPlaceholderValues(view.svgStore.query());

			// Update the "Review Updates" plugin.
			this.updateToolbar();
		}
	});
});
